// ═══════════════════════════════════════════════════════════════
// TemplateEditorModal.tsx – a 2026-os közzétételi lista szerkesztése (csak superadmin)
//
// - A főpontok címe, rövid neve, frissítése, megőrzése, leírása és külső linkjei írhatók át;
//   a főpontok száma és sorrendje rögzített. Az al-tételek (pl. „2.4 › Adóigazgatás”) felvehetők,
//   törölhetők, átírhatók.
// - A javítás az adott környezet adatbázisába kerül (DEV és LIVE külön), mentés után azonnal él.
//   Környezetek között az xlsx letöltés → visszatöltés viszi át. A gyári lista bármikor visszaállítható.
// - A már betöltött szervezetek bejegyzései nem változnak maguktól: Sablon – batch mód →
//   „Címek frissítése a sablon szerint”.
// ═══════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ApiClient, type TemplateChapter, type TemplateRecord } from '../../api/client';
import { downloadTemplateXlsx, parseTemplateXlsx, diffTemplates, normPont, isSub, recKey } from '../../utils/templateXlsx';
import { X, Save, Download, Upload, RotateCcw, Search, ChevronDown, ChevronRight, Plus, Trash2, AlertTriangle, CheckCircle2, RefreshCw, FilePenLine } from 'lucide-react';

type Rec = TemplateRecord & { _k: string };
type Chap = Omit<TemplateChapter, 'records'> & { records: Rec[] };

let seq = 0;
const withKeys = (chs: TemplateChapter[]): Chap[] => chs.map((c) => ({ ...c, records: c.records.map((r) => ({ ...r, _k: `k${++seq}` })) }));
const strip = (chs: Chap[]): TemplateChapter[] => chs.map((c) => ({ ...c, records: c.records.map(({ _k, ...r }) => r) }));

const inputCls = 'w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500';
const labelCls = 'block text-[11px] font-semibold text-slate-600 dark:text-slate-300 mb-1';

type SnippetRef = { id: string; name: string; has_html: boolean };
const SNIP_RE = /\{\{TARTALOM:([A-Za-z0-9_]+)\}\}/g;

const Field: React.FC<{ label: string; value?: string; onChange: (v: string) => void; rows?: number; changed?: boolean; mono?: boolean; hint?: string; snippets?: SnippetRef[] }> = ({ label, value, onChange, rows, changed, mono, hint, snippets }) => {
  const id = useMemo(() => `tef-${++seq}`, []);
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const refs = snippets ? [...new Set([...String(value || '').matchAll(SNIP_RE)].map((m) => m[1]))] : [];
  const insertRef = (sid: string) => {
    const tok = `{{TARTALOM:${sid}}}`;
    const cur = String(value || '');
    const el = ref.current;
    const at = el && typeof el.selectionStart === 'number' ? el.selectionStart : cur.length;
    const end = el && typeof el.selectionEnd === 'number' ? el.selectionEnd : at;
    onChange(cur.slice(0, at) + tok + cur.slice(end));
  };
  return (
    <div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <label htmlFor={id} className={labelCls}>
          {label}
          {changed && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 text-[9px] font-bold uppercase">módosítva</span>}
        </label>
        {snippets && (
          <select value="" aria-label={`${label}: tartalom-változó beszúrása`}
            onChange={(e) => { if (e.target.value) insertRef(e.target.value); }}
            className="mb-1 text-[11px] rounded-md border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 px-1.5 py-0.5 max-w-[16rem]">
            <option value="">＋ Tartalom-változó…</option>
            {snippets.map((sn) => <option key={sn.id} value={sn.id}>{sn.name}{sn.has_html ? ' (formázott)' : ''}</option>)}
            {!snippets.length && <option value="" disabled>Nincs globális tartalom-változó</option>}
          </select>
        )}
      </div>
      {rows ? (
        <textarea id={id} ref={ref} rows={rows} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={`${inputCls} ${mono ? 'font-mono text-[11px]' : ''} resize-y`} />
      ) : (
        <input id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={inputCls} />
      )}
      {refs.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1 text-[10px]">
          <span className="text-slate-500">Tartalom-változó:</span>
          {refs.map((r) => {
            const sn = snippets?.find((x) => x.id === r);
            return <span key={r} className={`px-1.5 py-0.5 rounded font-semibold ${sn ? 'bg-violet-100 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300' : 'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300'}`}
              title={`{{TARTALOM:${r}}}`}>{sn ? sn.name : `hiányzó (${r})`}</span>;
          })}
        </div>
      )}
      {hint && <p className="mt-0.5 text-[10px] text-slate-400">{hint}</p>}
    </div>
  );
};

export const TemplateEditorModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [work, setWork] = useState<Chap[]>([]);
  const [saved, setSaved] = useState<TemplateChapter[]>([]);
  const [factory, setFactory] = useState<TemplateChapter[]>([]);
  const [overridden, setOverridden] = useState(false);
  const [meta, setMeta] = useState<{ updated_at: string; updated_by: string } | null>(null);
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [onlyChanged, setOnlyChanged] = useState(false);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<string[]>([]);
  const [notice, setNotice] = useState<{ type: 'ok' | 'warn'; text: string; list?: string[] } | null>(null);
  const [source, setSource] = useState<'editor' | 'xlsx'>('editor');
  const fileRef = useRef<HTMLInputElement>(null);
  // a sablonba csak globális tartalom-változó hivatkozható
  const [globalSnippets, setGlobalSnippets] = useState<SnippetRef[]>([]);

  const apply = (r: Awaited<ReturnType<typeof ApiClient.getTemplateEditor>>) => {
    if (!r.ok || !r.chapters) { setErrors(r.errors?.length ? r.errors : [r.error || 'A lista nem tölthető be']); return false; }
    setWork(withKeys(r.chapters));
    setSaved(r.chapters);
    setFactory(r.factory || []);
    setOverridden(Boolean(r.overridden));
    setMeta(r.meta || null);
    setSource('editor');
    return true;
  };

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true); setErrors([]); setNotice(null); setOpen(new Set()); setSearch(''); setOnlyChanged(false);
    ApiClient.getTemplateEditor().then(apply).finally(() => setLoading(false));
    ApiClient.getContentSnippets().then((r) => setGlobalSnippets((r.data || []).filter((x) => x.scope === 'global').map((x) => ({ id: x.id, name: x.name, has_html: x.has_html }))));
  }, [isOpen]);

  const plain = useMemo(() => strip(work), [work]);
  const pending = useMemo(() => (saved.length ? diffTemplates(saved, plain) : { changed: [], added: [], removed: [], total: 0 }), [saved, plain]);
  const factoryIdx = useMemo(() => { const m = new Map<string, TemplateRecord>(); for (const ch of factory) for (const r of ch.records) m.set(recKey(r), r); return m; }, [factory]);
  const savedIdx = useMemo(() => { const m = new Map<string, TemplateRecord>(); for (const ch of saved) for (const r of ch.records) m.set(recKey(r), r); return m; }, [saved]);

  if (!isOpen) return null;

  const dirty = pending.total > 0;
  const tryClose = () => { if (!saving && (!dirty || window.confirm('Mentetlen módosítások vannak. Biztosan bezárod?'))) onClose(); };

  const update = (ci: number, k: string, patch: Partial<TemplateRecord>) =>
    setWork((w) => w.map((c, i) => (i !== ci ? c : { ...c, records: c.records.map((r) => (r._k === k ? { ...r, ...patch } : r)) })));
  const removeSub = (ci: number, k: string) => setWork((w) => w.map((c, i) => (i !== ci ? c : { ...c, records: c.records.filter((r) => r._k !== k) })));
  const addSub = (ci: number, main: string) => {
    const k = `k${++seq}`;
    setWork((w) => w.map((c, i) => {
      if (i !== ci) return c;
      const subs = c.records.filter((r) => isSub(r.pont) && normPont(r.pont).split(' › ')[0] === main);
      const lastGroup = subs.length ? normPont(subs[subs.length - 1].pont).split(' › ').slice(1).join(' › ') : 'Új csoport';
      const rec: Rec = { _k: k, pont: `${main} › ${lastGroup}`, cim: '', leiras: '', kulso_linkek: '' };
      const recs = [...c.records];
      let at = recs.findIndex((r) => !isSub(r.pont) && normPont(r.pont) === main);
      while (at + 1 < recs.length && isSub(recs[at + 1].pont)) at++;
      recs.splice(at + 1, 0, rec);
      return { ...c, records: recs };
    }));
    setOpen((o) => new Set(o).add(k));
  };

  const recChanged = (r: Rec) => { const s = savedIdx.get(recKey(r)); if (!s) return true; return ['cim', 'short', 'frissites', 'megorzesi_ido', 'leiras', 'kulso_linkek'].some((f) => String((s as any)[f] ?? '').trim() !== String((r as any)[f] ?? '').trim()); };
  const vsFactory = (r: Rec, f: keyof TemplateRecord) => { const fr = factoryIdx.get(recKey(r)); return fr ? String(fr[f] ?? '').trim() !== String(r[f] ?? '').trim() : false; };
  const differsFromFactory = (r: Rec) => !factoryIdx.has(recKey(r)) || (['cim', 'short', 'frissites', 'megorzesi_ido', 'leiras', 'kulso_linkek'] as const).some((f) => vsFactory(r, f));

  const save = async () => {
    setSaving(true); setErrors([]); setNotice(null);
    const r = await ApiClient.saveTemplateEditor(plain, source);
    if (apply(r)) setNotice({ type: 'ok', text: 'A lista elmentve – az új szervezetek és a sablonműveletek már ezt használják. A már betöltött szervezeteknél: Sablon – batch mód → „Címek frissítése a sablon szerint”.' });
    setSaving(false);
  };
  const resetFactory = async () => {
    if (!window.confirm('Visszaállítod a gyári listát? Az összes javítás elvész (az xlsx-et érdemes előbb letölteni).')) return;
    setSaving(true); setErrors([]);
    const r = await ApiClient.resetTemplateEditor();
    if (apply(r)) setNotice({ type: 'ok', text: 'A gyári lista visszaállítva.' });
    setSaving(false);
  };
  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    setErrors([]); setNotice(null);
    try {
      const res = await parseTemplateXlsx(f, plain);
      if (res.errors.length) { setErrors(res.errors); return; }
      const d = diffTemplates(saved, res.chapters);
      setWork(withKeys(res.chapters));
      setSource('xlsx');
      setNotice({
        type: d.total ? 'warn' : 'ok',
        text: d.total ? `XLSX betöltve: ${d.total} eltérés a mentett listához képest. Ellenőrizd, majd Mentés.` : 'XLSX betöltve: nincs eltérés a mentett listához képest.',
        list: [...res.warnings, ...d.changed.map((c) => `${c.pont}: ${c.fields.join(', ')}`), ...d.added.map((a) => `+ ${a}`), ...d.removed.map((a) => `− ${a}`)].slice(0, 80),
      });
    } catch {
      setErrors(['Az xlsx nem olvasható be.']);
    }
  };

  const ch = work[tab];
  const q = search.trim().toLowerCase();
  const match = (r: Rec) => !q || `${r.pont} ${r.cim} ${r.short || ''} ${r.leiras || ''}`.toLowerCase().includes(q);
  const mains = ch ? ch.records.filter((r) => !isSub(r.pont)) : [];
  const subsOf = (m: Rec) => (ch ? ch.records.filter((r) => isSub(r.pont) && normPont(r.pont).split(' › ')[0] === normPont(m.pont)) : []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs" role="dialog" aria-modal="true" aria-labelledby="te-title" onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); tryClose(); } }}>
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-5xl shadow-2xl max-h-[94vh] flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 id="te-title" className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2"><FilePenLine className="w-5 h-5 text-indigo-600" /> Sablon szerkesztése</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 flex flex-wrap items-center gap-2">
              <span>Közzétételi lista 2026 – Infotv. 1. melléklet</span>
              {overridden ? (
                <span className="px-2 py-0.5 rounded-full border text-[10px] font-semibold bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800">
                  Javított lista{meta ? ` · ${new Date(meta.updated_at).toLocaleString('hu-HU', { dateStyle: 'short', timeStyle: 'short' })} · ${meta.updated_by}` : ''}
                </span>
              ) : (
                <span className="px-2 py-0.5 rounded-full border text-[10px] font-semibold bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700">Gyári lista</span>
              )}
              <span className="text-[10px]">A javítás ennek a környezetnek az adatbázisába kerül; másik környezetbe xlsx-szel vihető át.</span>
            </p>
          </div>
          <button type="button" onClick={tryClose} aria-label="Bezárás" className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => downloadTemplateXlsx(plain)} disabled={loading || !work.length}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border border-emerald-300 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 cursor-pointer disabled:opacity-40">
            <Download className="w-3.5 h-3.5" /> XLSX letöltése
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={onFile} />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={loading || !work.length}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border border-cyan-300 dark:border-cyan-800 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-50 dark:hover:bg-cyan-950/40 cursor-pointer disabled:opacity-40">
            <Upload className="w-3.5 h-3.5" /> XLSX visszatöltése
          </button>
          <button type="button" onClick={resetFactory} disabled={loading || saving || !overridden}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer disabled:opacity-40"
            title="Az összes javítás törlése, a beépített (gyári) lista lesz érvényes">
            <RotateCcw className="w-3.5 h-3.5" /> Gyári lista visszaállítása
          </button>
          <div className="relative flex-1 min-w-[12rem]">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Keresés pontszámra, címre, leírásra…" aria-label="Keresés a listában"
              className="w-full pl-8 pr-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white" />
          </div>
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 cursor-pointer select-none">
            <input type="checkbox" checked={onlyChanged} onChange={(e) => setOnlyChanged(e.target.checked)} /> Csak a gyáritól eltérők
          </label>
        </div>

        <div className="px-5 pt-3 flex flex-wrap gap-1.5" role="tablist" aria-label="Fejezetek">
          {work.map((c, i) => (
            <button key={c.slug} type="button" role="tab" aria-selected={tab === i} onClick={() => setTab(i)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer ${tab === i ? 'bg-indigo-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'}`}>
              {c.pageTitle.replace(/^([0-9]+)\.\s*/, '$1. ')}
              <span className="ml-1 opacity-70 text-[10px]">{c.records.length}</span>
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-auto px-5 py-3 space-y-2">
          {loading && <div className="py-16 text-center text-slate-400 text-xs flex flex-col items-center gap-2"><RefreshCw className="w-5 h-5 animate-spin text-indigo-500" />A lista betöltése…</div>}
          {errors.length > 0 && (
            <div role="alert" className="p-3 rounded-xl border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 text-xs text-rose-800 dark:text-rose-300">
              <div className="font-semibold flex items-center gap-1.5 mb-1"><AlertTriangle className="w-4 h-4" /> A lista hibás – nem történt mentés</div>
              <ul className="list-disc pl-5 space-y-0.5">{errors.slice(0, 30).map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          )}
          {notice && (
            <div role="status" className={`p-3 rounded-xl border text-xs ${notice.type === 'ok' ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300' : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200'}`}>
              <div className="flex items-start justify-between gap-2">
                <span className="flex items-start gap-1.5"><CheckCircle2 className="w-4 h-4 shrink-0" />{notice.text}</span>
                <button type="button" onClick={() => setNotice(null)} aria-label="Értesítés bezárása" className="opacity-60 hover:opacity-100 cursor-pointer">✕</button>
              </div>
              {notice.list && notice.list.length > 0 && <ul className="mt-1.5 pl-5 list-disc max-h-32 overflow-auto space-y-0.5">{notice.list.map((x, i) => <li key={i}>{x}</li>)}</ul>}
            </div>
          )}

          {!loading && ch && mains.map((m) => {
            const subs = subsOf(m);
            const showSubs = subs.filter((s) => match(s) && (!onlyChanged || differsFromFactory(s)));
            if (!match(m) && !showSubs.length) return null;
            if (onlyChanged && !differsFromFactory(m) && !showSubs.length) return null;
            const isOpen = open.has(m._k);
            const toggle = () => setOpen((o) => { const n = new Set(o); if (n.has(m._k)) n.delete(m._k); else n.add(m._k); return n; });
            const fr = factoryIdx.get(recKey(m));
            return (
              <div key={m._k} className="rounded-xl border border-slate-200 dark:border-slate-800">
                <button type="button" onClick={toggle} aria-expanded={isOpen} className="w-full text-left px-3 py-2.5 flex items-start gap-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 rounded-xl">
                  {isOpen ? <ChevronDown className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />}
                  <span className="font-mono text-xs font-bold text-indigo-700 dark:text-indigo-300 w-10 shrink-0">{m.pont}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-semibold text-slate-900 dark:text-white">{m.short || '—'}</span>
                    <span className="block text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2">{m.cim}</span>
                  </span>
                  {recChanged(m) && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 shrink-0">mentetlen</span>}
                  {!recChanged(m) && differsFromFactory(m) && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300 shrink-0">javított</span>}
                  {subs.length > 0 && <span className="text-[10px] text-slate-400 shrink-0">{subs.length} al-tétel</span>}
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 pt-1 space-y-3 border-t border-slate-100 dark:border-slate-800">
                    <Field label="Adat (a pont címe)" rows={3} value={m.cim} changed={vsFactory(m, 'cim')} onChange={(v) => update(tab, m._k, { cim: v })} />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Field label="Rövid név (mappanév)" value={m.short} changed={vsFactory(m, 'short')} onChange={(v) => update(tab, m._k, { short: v })} />
                      <Field label="Frissítés" value={m.frissites} changed={vsFactory(m, 'frissites')} onChange={(v) => update(tab, m._k, { frissites: v })} />
                      <Field label="Megőrzés" value={m.megorzesi_ido} changed={vsFactory(m, 'megorzesi_ido')} onChange={(v) => update(tab, m._k, { megorzesi_ido: v })} />
                    </div>
                    <Field label="Leírás" rows={5} value={m.leiras} changed={vsFactory(m, 'leiras')} onChange={(v) => update(tab, m._k, { leiras: v })} snippets={globalSnippets} hint="{{TOKEN}} és {{BLOKK_…}} helyek a szervezet adataival töltődnek ki; a {{TARTALOM:…}} helyére betöltéskor a tartalom-változó kerül (formázottnál a stílusokkal)." />
                    <Field label="Külső linkek" rows={3} mono value={m.kulso_linkek} changed={vsFactory(m, 'kulso_linkek')} onChange={(v) => update(tab, m._k, { kulso_linkek: v })} hint="Soronként: Címke — URL" />
                    <div className="flex flex-wrap items-center gap-2">
                      {fr && differsFromFactory(m) && (
                        <button type="button" onClick={() => update(tab, m._k, { cim: fr.cim, short: fr.short, frissites: fr.frissites, megorzesi_ido: fr.megorzesi_ido, leiras: fr.leiras, kulso_linkek: fr.kulso_linkek })}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 cursor-pointer">
                          <RotateCcw className="w-3 h-3" /> A pont gyári értékei
                        </button>
                      )}
                      <button type="button" onClick={() => addSub(tab, normPont(m.pont))}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border border-indigo-300 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 cursor-pointer">
                        <Plus className="w-3 h-3" /> Új al-tétel
                      </button>
                    </div>
                  </div>
                )}
                {(isOpen || q || onlyChanged) && showSubs.length > 0 && (
                  <div className="px-3 pb-3 space-y-1.5">
                    {showSubs.map((s) => {
                      const sOpen = open.has(s._k);
                      const parts = normPont(s.pont).split(' › ');
                      const group = parts.slice(1).join(' › ');
                      return (
                        <div key={s._k} className="ml-6 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-950/40">
                          <div className="flex items-center gap-2 px-2.5 py-1.5">
                            <button type="button" aria-expanded={sOpen} onClick={() => setOpen((o) => { const n = new Set(o); if (n.has(s._k)) n.delete(s._k); else n.add(s._k); return n; })}
                              className="flex-1 min-w-0 text-left flex items-center gap-2 cursor-pointer">
                              {sOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                              <span className="text-[10px] text-slate-500 shrink-0">{group}</span>
                              <span className="text-xs text-slate-800 dark:text-slate-100 truncate">{s.cim || <em className="text-rose-500">megnevezés nélkül</em>}</span>
                              {!savedIdx.has(recKey(s)) && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 shrink-0">új / mentetlen</span>}
                            </button>
                            <button type="button" onClick={() => removeSub(tab, s._k)} aria-label={`${s.cim || 'al-tétel'} törlése`} title="Al-tétel törlése"
                              className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 cursor-pointer"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                          {sOpen && (
                            <div className="px-2.5 pb-2.5 space-y-2">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                <Field label="Csoport" value={group} onChange={(v) => update(tab, s._k, { pont: `${parts[0]} › ${v}` })} />
                                <div className="md:col-span-2"><Field label="Megnevezés" value={s.cim} onChange={(v) => update(tab, s._k, { cim: v })} /></div>
                              </div>
                              <Field label="Leírás" rows={4} value={s.leiras} onChange={(v) => update(tab, s._k, { leiras: v })} snippets={globalSnippets} />
                              <Field label="Külső linkek" rows={2} mono value={s.kulso_linkek} onChange={(v) => update(tab, s._k, { kulso_linkek: v })} hint="Soronként: Címke — URL" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-800 flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-slate-500 dark:text-slate-400" aria-live="polite">
            {dirty
              ? `Mentetlen: ${pending.changed.length} módosított, ${pending.added.length} új, ${pending.removed.length} törölt tétel${source === 'xlsx' ? ' (xlsx-ből)' : ''}`
              : 'Nincs mentetlen módosítás'}
            {' · A főpontok száma és sorrendje rögzített, az al-tételek szabadon szerkeszthetők.'}
          </span>
          <div className="flex gap-2">
            <button type="button" onClick={tryClose} disabled={saving} className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl cursor-pointer">Bezárás</button>
            <button type="button" onClick={save} disabled={saving || loading || !dirty}
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-500 cursor-pointer disabled:opacity-40">
              {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Mentés
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditorModal;
